import { useEffect, useMemo, useState } from 'react'
import { daysWithSales } from '../../services/orders'
import { ChevronDownIcon, ChevronUpIcon } from '../icons'
import { formatDayKey } from '../../utils/dayKey'

interface Props {
  tenantId: string
  /** 'YYYY-MM-DD' */
  value: string
  onPick: (dayKey: string) => void
  onClose: () => void
  /**
   * Aralık kipi (22 Eki 2026): tek takvim, ilk basış başlangıç, ikinci basış
   * bitiş. Verilince `onPick` kullanılmıyor; `value` takvimin açılacağı ay.
   */
  range?: { from: string; to: string }
  onPickRange?: (from: string, to: string) => void
}

const MONTHS = [
  'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık',
]

/** Monday first, as the week is read here. */
const WEEKDAYS = ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pz']

const pad = (n: number) => String(n).padStart(2, '0')
const keyOf = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`

/**
 * Picks which day the report shows.
 *
 * Days that took money are marked, so the question "when was the last busy day"
 * is answered by looking rather than by opening one date after another.
 * Without the marks, finding one means guessing.
 */
export function DayPicker({ tenantId, value, onPick, onClose, range, onPickRange }: Props) {
  const [y, mo] = useMemo(() => {
    const [yy, mm] = value.split('-').map(Number)
    return [yy, mm - 1]
  }, [value])

  const [year, setYear] = useState(y)
  const [month, setMonth] = useState(mo)
  const [sold, setSold] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(true)
  const ranged = Boolean(onPickRange)
  /** Aralıkta ilk basılan gün; ikinci basışı bekliyor. */
  const [anchor, setAnchor] = useState<string | null>(null)
  /** İkinci basılan gün: aralık tamam, "Göster" bekliyor. */
  const [end, setEnd] = useState<string | null>(null)
  /** İkinci basıştan önce fareyle üzerinde durulan gün: aralık önizlemesi. */
  const [hover, setHover] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    setBusy(true)
    daysWithSales(tenantId, `${year}-${pad(month + 1)}`)
      .then((s) => live && setSold(s))
      .catch((err) => {
        console.error('[report] month scan', err)
        // A failed scan leaves the grid unmarked rather than empty: every day
        // is still reachable, only the shortcut is missing.
        if (live) setSold(new Set())
      })
      .finally(() => live && setBusy(false))
    return () => {
      live = false
    }
  }, [tenantId, year, month])

  /** Leading blanks so the 1st lands under its weekday. */
  const lead = useMemo(() => {
    const jsDay = new Date(year, month, 1).getDay() // 0 = Sunday
    return (jsDay + 6) % 7
  }, [year, month])

  const days = new Date(year, month + 1, 0).getDate()
  const today = (() => {
    const d = new Date()
    return keyOf(d.getFullYear(), d.getMonth(), d.getDate())
  })()

  // Görünen aralık: seçim sürerken başlangıç ile fare arası, yoksa mevcut aralık.
  const shown = (() => {
    if (!ranged) return null
    if (anchor) {
      const other = end ?? hover ?? anchor
      return anchor <= other ? { from: anchor, to: other } : { from: other, to: anchor }
    }
    return range ?? null
  })()

  // İkinci basışta takvim kapanmıyor (kullanıcı, 22 Eki 2026): aralık
  // işaretli kalıyor, yanlışsa bir güne basmak seçimi baştan başlatıyor,
  // rapor "Göster"le açılıyor.
  const pickDay = (key: string) => {
    if (!ranged) return onPick(key)
    if (!anchor || end) {
      setAnchor(key)
      setEnd(null)
      setHover(null)
      return
    }
    setEnd(key)
  }

  const step = (by: number) => {
    const m = month + by
    if (m < 0) {
      setYear(year - 1)
      setMonth(11)
    } else if (m > 11) {
      setYear(year + 1)
      setMonth(0)
    } else {
      setMonth(m)
    }
  }

  return (
    <div className="dlg" role="dialog" aria-modal="true">
      <div className="dlg__card dlg__card--cal">
        <div className="cal__head">
          <button type="button" onClick={() => step(-1)} aria-label="Önceki ay">
            <ChevronUpIcon />
          </button>
          <h2>
            {MONTHS[month]} {year}
          </h2>
          <button type="button" onClick={() => step(1)} aria-label="Sonraki ay">
            <ChevronDownIcon />
          </button>
        </div>

        <div className="cal__week">
          {WEEKDAYS.map((d) => (
            <span key={d}>{d}</span>
          ))}
        </div>

        <div className={`cal__grid ${busy ? 'is-busy' : ''}`}>
          {Array.from({ length: lead }, (_, i) => (
            <span key={`b${i}`} />
          ))}
          {Array.from({ length: days }, (_, i) => {
            const d = i + 1
            const key = keyOf(year, month, d)
            const has = sold.has(key)
            const isEnd = shown ? key === shown.from || key === shown.to : key === value
            const inside = shown ? key > shown.from && key < shown.to : false
            // Aralıkta gelecek gün yok: rapor bugünden ileriye bakamaz.
            const future = ranged && key > today
            return (
              <button
                key={key}
                type="button"
                className={`cal__day ${has ? 'has-sales' : ''} ${isEnd ? 'is-on' : ''} ${
                  inside ? 'is-in' : ''
                } ${key === today ? 'is-today' : ''}`}
                disabled={future}
                onClick={() => pickDay(key)}
                onPointerEnter={() => anchor && !end && setHover(key)}
                // Days with nothing on them stay pressable: an empty report is
                // a real answer, and greying them out invites a second guess
                // about whether the marking itself is working.
                title={has ? 'Satış var' : 'Satış yok'}
              >
                {d}
              </button>
            )
          })}
        </div>

        {ranged && (
          <p className="cal__hint">
            {!anchor
              ? 'Başlangıç gününe basın'
              : !end
                ? 'Bitiş gününe basın'
                : shown!.from === shown!.to
                  ? formatDayKey(shown!.from)
                  : `${formatDayKey(shown!.from)} - ${formatDayKey(shown!.to)}`}
          </p>
        )}

        <p className="cal__legend">
          <span className="cal__dot" /> Satış olan günler
        </p>

        <div className="dlg__buttons dlg__buttons--even">
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Kapat
          </button>
          {ranged ? (
            <button
              type="button"
              className="btn btn--primary"
              disabled={!(anchor && end)}
              onClick={() => shown && anchor && end && onPickRange!(shown.from, shown.to)}
            >
              Göster
            </button>
          ) : (
            <button type="button" className="btn btn--primary" onClick={() => onPick(today)}>
              Bugün
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
